/**
 * useDashboardLayout — persystencja układu pulpitu (jeden układ per user).
 *
 * Wzorzec strukturalny z `configurable-table/useTableLayout.ts`, ale prostszy:
 * backend trzyma JEDEN aktywny wiersz per user (iter. 1), więc bez listy/wyboru
 * układów. `GET /dashboard/layout` → `{ layout, templateName }` lub oba `null`
 * (świeży user — kontener stosuje wtedy `buildDefaultLayout(t)`, bez zapisu
 * dopóki user nie kliknie Zapisz). `PUT` upsertuje cały dokument.
 */

import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

import type { DashboardLayoutDoc } from './types';

const QUERY_KEY = ['tenant', 'dashboard', 'layout'] as const;

interface LayoutResponse {
  layout: DashboardLayoutDoc | null;
  templateName: string | null;
  /** Skąd pochodzi układ: własny zapis usera, domyślny tenanta albo wbudowany systemowy. */
  source?: 'personal' | 'tenant_default' | 'builtin';
}

export interface UseDashboardLayoutReturn {
  layout: DashboardLayoutDoc | null;
  templateName: string | null;
  /** `personal` = user ma własny zapisany układ (można go zresetować do domyślnego). */
  source: 'personal' | 'tenant_default' | 'builtin';
  isLoading: boolean;
  saveLayout: (layout: DashboardLayoutDoc, templateName?: string) => Promise<void>;
  isSaving: boolean;
  /** Kasuje własny układ — po odświeżeniu pulpit wraca do domyślnego tenanta / systemowego. */
  resetLayout: () => Promise<void>;
  isResetting: boolean;
}

export function useDashboardLayout(): UseDashboardLayoutReturn {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await api.get<LayoutResponse>('/dashboard/layout');
      return res.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minut
  });

  const saveMutation = useMutation({
    mutationFn: async (input: { layout: DashboardLayoutDoc; templateName?: string }) => {
      const res = await api.put('/dashboard/layout', input);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const resetMutation = useMutation({
    mutationFn: async () => {
      const res = await api.delete('/dashboard/layout');
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const saveLayout = useCallback(
    async (layout: DashboardLayoutDoc, templateName?: string) => {
      await saveMutation.mutateAsync({ layout, templateName });
    },
    [saveMutation],
  );

  const resetLayout = useCallback(async () => {
    await resetMutation.mutateAsync();
  }, [resetMutation]);

  return {
    layout: query.data?.layout ?? null,
    templateName: query.data?.templateName ?? null,
    source: query.data?.source ?? 'builtin',
    isLoading: query.isLoading,
    saveLayout,
    isSaving: saveMutation.isPending,
    resetLayout,
    isResetting: resetMutation.isPending,
  };
}
